import { AxiosResponse } from "axios";
import {
  BaseResponse,
  CreateDepositRefundBody,
  CreateDepositRefundItem,
  CreateDepositRefundRequestBody,
  CreateDepositRefundResult,
  DepositRefundDeduct,
  DepositRefundDeductDefault,
  DepositRefundDetail,
  DepositRefundFile,
  DepositRefundLease,
  DepositRefundLog,
  DepositRefundMethod,
  DepositRefundOwner,
  DepositRefundParams,
  DepositRefundReceiptParams,
  DepositRefundReceiptRow,
  DepositRefundRow,
  DepositRefundSide,
  DepositRefundStallOption,
  DepositRefundStatus,
  DepositRefundSummary,
  DepositRefundZoneOption,
  Paginated,
} from "./deposit-refund.api";

/**
 * Mock ของเมนูคืนค่ามัดจำแผง — ใช้ตอน backend ยังไม่พร้อม
 * shape ทุก field ตรงตาม deposit-refund-api.md รวมถึงจุดที่ backend คืน DECIMAL
 * เป็น string และ DATE เป็น UTC ISO เพื่อให้หน้าจอเจอสภาพเดียวกับของจริง
 *
 * store เป็น module scope และแก้ค่าจริงเมื่อ create / review / cancel / remove
 * → ตารางอัปเดตตามหลังทำรายการ ใช้เดโมได้ครบ flow (รีเซ็ตเมื่อ reload หน้า)
 */

/** หน่วงเวลาให้ loading state / isMounted guard / debounce ถูกทดสอบจริง */
const DELAY_MS = 260;

/** ผู้เช่าที่ mock สมมติว่าเป็นคนที่ login อยู่ (API จริงอ่านจาก token) */
const MOCK_CUSTOMER_USER_ID = 548;
const MOCK_CUSTOMER_ID = 422;
const MOCK_CUSTOMER_NAME = "เทพพิทักษ์ คงทิพย์";
/** เจ้าหน้าที่ที่ mock สมมติว่าเป็นคนทำรายการ */
const MOCK_STAFF_ID = 43;
const MOCK_STAFF_NAME = "Developer V2n";
/**
 * คำร้องที่ mock สมมติว่าสัญญาใบนี้เคยถูกเรียกคืนแผงค้าและอนุมัติพร้อมยอดหักแล้ว
 * ใช้เทสต์ flow prefill — ใบอื่นจะได้ has_default = false
 */
const MOCK_DEDUCT_DEFAULT_ID = 12;

const sleep = () => new Promise((resolve) => setTimeout(resolve, DELAY_MS));

/** DECIMAL ของ MySQL มาเป็น string เสมอ — mock ต้องเลียนแบบ ไม่ใช่ส่ง number */
const dec = (value: number) => value.toFixed(2);

/** คืน copy กันหน้าจอ mutate ค่าใน store โดยไม่ตั้งใจ */
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

/**
 * ปลอม AxiosResponse ให้ครบ field ที่ type ต้องการ
 * AxiosRequestConfig ทุก key เป็น optional จึงใส่ {} ได้ ไม่ต้อง cast
 */
const ok = <T,>(data: T): AxiosResponse<BaseResponse<T>> => ({
  data: { success: true, status: 200, message: "success", data },
  status: 200,
  statusText: "OK",
  headers: {},
  config: {},
});

/**
 * ปลอม error ให้มีรูปเดียวกับที่ axios โยน เพื่อให้ getApiErrorMessage / getApiErrorStatus
 * อ่านได้เหมือนของจริง (ต้องมี response ไม่งั้นถูกตีเป็น network timeout)
 */
const fail = (status: number, message: string) => {
  const error: any = new Error(message);
  error.isAxiosError = true;
  error.response = {
    status,
    statusText: "",
    headers: {},
    config: {},
    data: { error: "Bad Request", errorDescription: message, statusCode: status },
  };
  return error;
};

/* ------------------------------------------------------------------ *
 * ข้อมูลตั้งต้น
 * ------------------------------------------------------------------ */

const ZONES: DepositRefundZoneOption[] = [
  { zoneId: 72, zone_name: "โซน 2" },
  { zoneId: 65, zone_name: "โซน 3" },
  { zoneId: 80, zone_name: "โซน 6 อาหารทะเล" },
  { zoneId: 91, zone_name: "โซน ลานอเนกประสงค์" },
];

const zoneName = (zoneId: number | null) =>
  ZONES.find((zone) => zone.zoneId === zoneId)?.zone_name || null;

/** ตัวนับ id ต่อจากค่าที่ seed ไว้ */
let nextRefundId = 100;
let nextLogId = 200;
let nextFileId = 100;
let nextRefundNo = 100;

/** เก็บ File จริงที่แนบตอนพิจารณา เพื่อให้ downloadFile คืน blob ที่เปิดได้ */
const uploadedFiles = new Map<number, File>();

type SeedRow = {
  id: number;
  no: string;
  leaseAgreementId: number;
  lease_agreement_no: string;
  userId: number;
  customerId: number;
  tenant_name: string;
  zoneId: number;
  stallId: number;
  stall_name: string;
  stall_number: string;
  stall_type_name: string;
  move_out_penalty_rate: number;
  damage_insurance_limit: number;
  /** ยอดหักที่เจ้าหน้าที่กรอกตอนพิจารณา (null = ยังไม่พิจารณา / ไม่อนุมัติ) */
  deduct_move_out: number | null;
  deduct_damage: number | null;
  refund_method: DepositRefundMethod | null;
  request_note: string | null;
  request_date: string;
  /** null = ลูกค้ายื่นเอง */
  request_by_staff: boolean;
  reviewed_date: string | null;
  review_note: string | null;
  refund_date: string | null;
  doc_ref: string | null;
  status: DepositRefundStatus;
};

/** 12 คำร้อง ครบ 4 สถานะ และมากพอให้เห็น pagination 2 หน้าที่ limit 10 */
const SEED: SeedRow[] = [
  {
    id: 12, no: "DEP-2569-0012",
    leaseAgreementId: 277, lease_agreement_no: "ตลาดสด 34/2567",
    userId: MOCK_CUSTOMER_USER_ID, customerId: MOCK_CUSTOMER_ID, tenant_name: MOCK_CUSTOMER_NAME,
    zoneId: 72, stallId: 1595, stall_name: "ร้านตี๋ ปูทะเล", stall_number: "2/24",
    stall_type_name: "อาหารทะเล",
    move_out_penalty_rate: 529.14, damage_insurance_limit: 4969,
    deduct_move_out: null, deduct_damage: null,
    refund_method: null, request_note: null,
    request_date: "2026-09-08T03:40:50.000Z", request_by_staff: false,
    reviewed_date: null, review_note: null, refund_date: null, doc_ref: null,
    status: "WAITING",
  },
  {
    id: 11, no: "DEP-2569-0011",
    leaseAgreementId: 576, lease_agreement_no: "ตลาดสด 90/2567",
    userId: MOCK_CUSTOMER_USER_ID, customerId: MOCK_CUSTOMER_ID, tenant_name: MOCK_CUSTOMER_NAME,
    zoneId: 65, stallId: 1602, stall_name: "ร้านตี๋ ของฝาก", stall_number: "3/07",
    stall_type_name: "ของฝาก",
    move_out_penalty_rate: 289, damage_insurance_limit: 2700,
    deduct_move_out: 100, deduct_damage: 200,
    refund_method: "BANK_TRANSFER", request_note: "ทดสอบยื่นแทนลูกค้า",
    request_date: "2026-08-28T02:15:00.000Z", request_by_staff: true,
    reviewed_date: "2026-09-02T04:42:54.000Z",
    review_note: "ตรวจรับคืนพื้นที่เรียบร้อย หักค่าปรับ 100 และค่าเสียหาย 200",
    refund_date: "2026-09-14T17:00:00.000Z", doc_ref: "REF-2569-001",
    status: "APPROVE",
  },
  {
    id: 10, no: "DEP-2569-0010",
    leaseAgreementId: 601, lease_agreement_no: "ตลาดสด 12/2566",
    userId: MOCK_CUSTOMER_USER_ID, customerId: MOCK_CUSTOMER_ID, tenant_name: MOCK_CUSTOMER_NAME,
    zoneId: 80, stallId: 1700, stall_name: "ร้านตี๋ ทะเลสด", stall_number: "6/12",
    stall_type_name: "อาหารทะเล",
    move_out_penalty_rate: 450, damage_insurance_limit: 3800,
    deduct_move_out: null, deduct_damage: null,
    refund_method: null, request_note: null,
    request_date: "2026-08-12T06:30:00.000Z", request_by_staff: false,
    reviewed_date: "2026-08-18T08:10:00.000Z",
    review_note: "ยังมีค่าเช่าค้างชำระ 3 งวด ไม่อนุมัติคืนเงินมัดจำ",
    refund_date: null, doc_ref: null,
    status: "NOT_APPROVE",
  },
  {
    id: 9, no: "DEP-2569-0009",
    leaseAgreementId: 610, lease_agreement_no: "ตลาดสด 45/2566",
    userId: MOCK_CUSTOMER_USER_ID, customerId: MOCK_CUSTOMER_ID, tenant_name: MOCK_CUSTOMER_NAME,
    zoneId: 91, stallId: 1755, stall_name: "ร้านตี๋ ดอกไม้", stall_number: "L/03",
    stall_type_name: "ดอกไม้",
    move_out_penalty_rate: 320, damage_insurance_limit: 2900,
    deduct_move_out: null, deduct_damage: null,
    refund_method: null, request_note: null,
    request_date: "2026-08-05T02:00:00.000Z", request_by_staff: false,
    reviewed_date: null, review_note: null, refund_date: null, doc_ref: null,
    status: "CANCELED",
  },
  {
    id: 8, no: "DEP-2569-0008",
    leaseAgreementId: 288, lease_agreement_no: "ลก.04/2567",
    userId: 701, customerId: 505, tenant_name: "วสีรจิตร จันทร์ขจร",
    zoneId: 72, stallId: 5001, stall_name: "ร้านจันทร์ขจรการเกษตร", stall_number: "A1",
    stall_type_name: "สินค้าอุปโภค",
    move_out_penalty_rate: 4500, damage_insurance_limit: 40500,
    deduct_move_out: null, deduct_damage: null,
    refund_method: null, request_note: null,
    request_date: "2026-07-20T02:12:00.000Z", request_by_staff: false,
    reviewed_date: null, review_note: null, refund_date: null, doc_ref: null,
    status: "WAITING",
  },
  {
    id: 7, no: "DEP-2569-0007",
    leaseAgreementId: 289, lease_agreement_no: "ศก.14/2567",
    userId: 702, customerId: 506, tenant_name: "ประยุร บัวสุ่ม",
    zoneId: 65, stallId: 5002, stall_name: "ร้านบัวสุ่มการเกษตร", stall_number: "A14",
    stall_type_name: "สินค้าอุปโภค",
    move_out_penalty_rate: 1800, damage_insurance_limit: 16200,
    deduct_move_out: null, deduct_damage: null,
    refund_method: null, request_note: null,
    request_date: "2026-07-17T03:48:00.000Z", request_by_staff: false,
    reviewed_date: null, review_note: null, refund_date: null, doc_ref: null,
    status: "WAITING",
  },
  {
    id: 6, no: "DEP-2569-0006",
    leaseAgreementId: 290, lease_agreement_no: "อท.77/2564",
    userId: 703, customerId: 507, tenant_name: "ประเสริฐ ใจดี",
    zoneId: 80, stallId: 5004, stall_name: "ร้านใจดีอาหารทะเลสด", stall_number: "C18",
    stall_type_name: "อาหารทะเล",
    move_out_penalty_rate: 3000, damage_insurance_limit: 27000,
    deduct_move_out: 0, deduct_damage: 0,
    refund_method: "CASH", request_note: null,
    request_date: "2026-07-09T06:26:00.000Z", request_by_staff: false,
    reviewed_date: "2026-07-15T04:12:00.000Z",
    review_note: "อนุมัติคืนเงินมัดจำเต็มจำนวน",
    refund_date: "2026-07-20T17:00:00.000Z", doc_ref: "ฎกทท.1188/2569",
    status: "APPROVE",
  },
  {
    id: 5, no: "DEP-2569-0005",
    leaseAgreementId: 291, lease_agreement_no: "ลอ.501/2566",
    userId: 704, customerId: 508, tenant_name: "สมร อุดมลาภ",
    zoneId: 91, stallId: 5005, stall_name: "ร้านอุดมลาภดอกไม้สด", stall_number: "B76",
    stall_type_name: "ดอกไม้",
    move_out_penalty_rate: 1200, damage_insurance_limit: 10800,
    deduct_move_out: 0, deduct_damage: 500,
    refund_method: "CHEQUE", request_note: null,
    request_date: "2026-07-02T01:55:00.000Z", request_by_staff: true,
    reviewed_date: "2026-07-08T08:47:00.000Z",
    review_note: "หักค่าซ่อมแซมพื้นที่ 500 บาท",
    refund_date: "2026-07-14T17:00:00.000Z", doc_ref: "ฎกทท.1102/2569",
    status: "APPROVE",
  },
  {
    id: 4, no: "DEP-2569-0004",
    leaseAgreementId: 292, lease_agreement_no: "รย.233/2565",
    userId: 705, customerId: 509, tenant_name: "เพ็ญศรี วงศ์คำ",
    zoneId: 65, stallId: 5006, stall_name: "ร้านวงศ์คำของฝาก", stall_number: "D07",
    stall_type_name: "ของฝาก",
    move_out_penalty_rate: 900, damage_insurance_limit: 8100,
    deduct_move_out: null, deduct_damage: null,
    refund_method: null, request_note: null,
    request_date: "2026-06-25T08:04:00.000Z", request_by_staff: false,
    reviewed_date: "2026-07-01T03:29:00.000Z",
    review_note: "เอกสารประกอบไม่ครบ กรุณายื่นคำร้องใหม่พร้อมสำเนาบัญชีธนาคาร",
    refund_date: null, doc_ref: null,
    status: "NOT_APPROVE",
  },
  {
    id: 3, no: "DEP-2569-0003",
    leaseAgreementId: 293, lease_agreement_no: "อป.189/2565",
    userId: 706, customerId: 510, tenant_name: "ธนกร มณีรัตน์",
    zoneId: 72, stallId: 5007, stall_name: "ร้านมณีรัตน์แปรรูป", stall_number: "B216",
    stall_type_name: "อาหารแปรรูป",
    move_out_penalty_rate: 2200, damage_insurance_limit: 19800,
    deduct_move_out: 0, deduct_damage: 0,
    refund_method: "BANK_TRANSFER", request_note: null,
    request_date: "2026-06-18T04:37:00.000Z", request_by_staff: false,
    reviewed_date: "2026-06-24T06:41:00.000Z", review_note: null,
    refund_date: "2026-06-30T17:00:00.000Z", doc_ref: "ฎกทท.1044/2569",
    status: "APPROVE",
  },
  {
    id: 2, no: "DEP-2569-0002",
    leaseAgreementId: 294, lease_agreement_no: "อท.78/2564",
    userId: 707, customerId: 511, tenant_name: "รัตนา สุขสวัสดิ์",
    zoneId: 80, stallId: 5008, stall_name: "ร้านสุขสวัสดิ์ทะเลสด", stall_number: "C19",
    stall_type_name: "อาหารทะเล",
    move_out_penalty_rate: 3000, damage_insurance_limit: 27000,
    deduct_move_out: null, deduct_damage: null,
    refund_method: null, request_note: null,
    request_date: "2026-06-11T07:19:00.000Z", request_by_staff: false,
    reviewed_date: null, review_note: null, refund_date: null, doc_ref: null,
    status: "WAITING",
  },
  {
    id: 1, no: "DEP-2569-0001",
    leaseAgreementId: 295, lease_agreement_no: "ลอ.502/2566",
    userId: 708, customerId: 512, tenant_name: "ชัยวัฒน์ ศรีสุข",
    zoneId: 91, stallId: 5009, stall_name: "ร้านศรีสุขไม้ดอกไม้ประดับ", stall_number: "B77",
    stall_type_name: "ดอกไม้",
    move_out_penalty_rate: 1200, damage_insurance_limit: 10800,
    deduct_move_out: null, deduct_damage: null,
    refund_method: null, request_note: null,
    request_date: "2026-06-04T02:41:00.000Z", request_by_staff: false,
    reviewed_date: null, review_note: null, refund_date: null, doc_ref: null,
    status: "CANCELED",
  },
];

const METHOD_NAME: Record<string, string> = {
  BANK_TRANSFER: "โอนเข้าบัญชีธนาคาร",
  CASH: "เงินสด",
  CHEQUE: "เช็คธนาคาร",
};

const DEDUCT_LABEL = {
  MOVE_OUT_PENALTY: "ค่าปรับกรณีไม่ยอมออกจากพื้นที่",
  DAMAGE_INSURANCE: "วงเงินประกันความเสียหาย",
};

/** สร้าง deducts จากยอดหักที่เก็บไว้ — [] เมื่อยังไม่พิจารณา หรือไม่อนุมัติ */
const buildDeducts = (row: SeedRow): DepositRefundDeduct[] => {
  if (row.status !== "APPROVE" || row.deduct_move_out === null || row.deduct_damage === null) {
    return [];
  }
  const lines: [DepositRefundDeduct["deduct_type"], number, number][] = [
    ["MOVE_OUT_PENALTY", row.move_out_penalty_rate, row.deduct_move_out],
    ["DAMAGE_INSURANCE", row.damage_insurance_limit, row.deduct_damage],
  ];
  return lines.map(([type, base, deduct], index) => {
    // server clamp ยอดหักไม่ให้เกินยอดตั้งต้น
    const applied = Math.min(deduct, base);
    return {
      id_deposit_refund_deduct: row.id * 10 + index,
      seq: index + 1,
      deduct_type: type,
      label: DEDUCT_LABEL[type],
      base_amount: dec(base),
      deduct_amount: dec(applied),
      remain_amount: dec(base - applied),
    };
  });
};

const requestAmount = (row: SeedRow) => row.move_out_penalty_rate + row.damage_insurance_limit;

const approvedAmount = (row: SeedRow): string | null => {
  const deducts = buildDeducts(row);
  if (!deducts.length) return null;
  return dec(deducts.reduce((sum, line) => sum + Number(line.remain_amount), 0));
};

const toDetail = (row: SeedRow): DepositRefundDetail => ({
  id_deposit_refund: row.id,
  deposit_refund_no: row.no,
  branchId: 1,
  leaseAgreementId: row.leaseAgreementId,
  lease_agreement_no: row.lease_agreement_no,
  userId: row.userId,
  customerId: row.customerId,
  tenant_name: row.tenant_name,
  tenant_idCard: "577xxxxxxx720",
  tenant_mobilePhone: "081xxx3xxx",
  zoneId: row.zoneId,
  zone_name: zoneName(row.zoneId),
  stallId: row.stallId,
  stall_name: row.stall_name,
  stall_number: row.stall_number,
  stall_type_name: row.stall_type_name,
  lease_date_end: "2027-07-28T17:00:00.000Z",
  lease_status: "ACTIVE",
  lease_status_name: "สัญญาเช่า",
  move_out_penalty_rate: row.move_out_penalty_rate,
  damage_insurance_limit: row.damage_insurance_limit,
  request_amount: dec(requestAmount(row)),
  refund_method: row.refund_method,
  refund_method_name: row.refund_method ? METHOD_NAME[row.refund_method] : null,
  request_note: row.request_note,
  request_date: row.request_date,
  request_by_id: row.request_by_staff ? MOCK_STAFF_ID : row.userId,
  request_by_name: row.request_by_staff ? MOCK_STAFF_NAME : row.tenant_name,
  reviewed_date: row.reviewed_date,
  reviewed_id: row.reviewed_date ? MOCK_STAFF_ID : null,
  reviewed_name: row.reviewed_date ? MOCK_STAFF_NAME : null,
  review_note: row.review_note,
  approved_amount: approvedAmount(row),
  refund_date: row.refund_date,
  doc_ref: row.doc_ref,
  status: row.status,
  deducts: buildDeducts(row),
});

const toRow = (detail: DepositRefundDetail): DepositRefundRow => ({
  id_deposit_refund: detail.id_deposit_refund,
  deposit_refund_no: detail.deposit_refund_no,
  leaseAgreementId: detail.leaseAgreementId,
  lease_agreement_no: detail.lease_agreement_no,
  userId: detail.userId,
  customerId: detail.customerId,
  tenant_name: detail.tenant_name,
  zoneId: detail.zoneId,
  zone_name: detail.zone_name,
  stallId: detail.stallId,
  stall_name: detail.stall_name,
  stall_number: detail.stall_number,
  stall_type_name: detail.stall_type_name,
  request_amount: detail.request_amount,
  approved_amount: detail.approved_amount,
  refund_method: detail.refund_method,
  refund_method_name: detail.refund_method_name,
  request_date: detail.request_date,
  reviewed_date: detail.reviewed_date,
  reviewed_id: detail.reviewed_id,
  reviewed_name: detail.reviewed_name,
  status: detail.status,
});

/** store หลัก — คัดลอก SEED ไว้ ไม่แก้ SEED ตรงๆ เพื่อให้ reload ได้ค่าตั้งต้น */
const store: DepositRefundDetail[] = SEED.map(toDetail);

/** ประวัติ — มีแถว CREATE ทุกใบตั้งแต่ยื่น ตามที่ spec ข้อ 3.9 ระบุ */
const logStore: DepositRefundLog[] = [];

SEED.forEach((row) => {
  const detail = store.find((item) => item.id_deposit_refund === row.id) as DepositRefundDetail;

  nextLogId += 1;
  logStore.push({
    id_deposit_refund_log: nextLogId,
    id_deposit_refund: row.id,
    action: "CREATE",
    result: null,
    note: row.request_note,
    action_id: detail.request_by_id,
    action_name: detail.request_by_name,
    action_date: row.request_date,
    deducts: [],
    files: [],
  });

  if (row.reviewed_date) {
    nextLogId += 1;
    logStore.push({
      id_deposit_refund_log: nextLogId,
      id_deposit_refund: row.id,
      action: "REVIEW",
      result: row.status === "APPROVE" ? "APPROVE" : "NOT_APPROVE",
      note: row.review_note,
      approved_amount: detail.approved_amount,
      // ใน log backend คืนเป็น 'YYYY-MM-DD' ไม่ใช่ ISO เหมือน detail
      refund_date: row.refund_date ? row.refund_date.slice(0, 10) : null,
      doc_ref: row.doc_ref,
      action_id: MOCK_STAFF_ID,
      action_name: MOCK_STAFF_NAME,
      action_date: row.reviewed_date,
      deducts: detail.deducts,
      // ใส่ไฟล์แนบไว้ใบเดียวพอให้เห็น UI ปุ่มดู/ดาวน์โหลด
      files:
        row.id === 11
          ? [
              {
                id_deposit_refund_file: 1,
                file_name: "1788864174238-ใบตรวจรับคืนพื้นที่.pdf",
                mimetype: "application/pdf",
                file_size: "82",
                download_url: "/deposit-refund/11/file/1/download",
              },
            ]
          : [],
    });
  }

  if (row.status === "CANCELED") {
    nextLogId += 1;
    logStore.push({
      id_deposit_refund_log: nextLogId,
      id_deposit_refund: row.id,
      action: "CANCEL",
      result: null,
      note: null,
      action_id: row.userId,
      action_name: row.tenant_name,
      action_date: row.request_date,
      deducts: [],
      files: [],
    });
  }
});

/** สัญญาที่มีเงินมัดจำ — ของผู้เช่าที่ mock ถือเป็นคน login (userId 548) */
/** field ที่คำนวณจาก store ตอนคืนค่า ไม่ต้อง seed */
type SeedLease = Omit<
  DepositRefundLease,
  "deposit_amount" | "can_request" | "blocked_by_no" | "blocked_by_status" | "blocked_reason"
>;

const LEASES: SeedLease[] = [
  {
    leaseAgreementId: 277, branchId: 1, userId: MOCK_CUSTOMER_USER_ID,
    lease_agreement_no: "ตลาดสด 34/2567", tenant_name: MOCK_CUSTOMER_NAME,
    stallId: 1595, zoneId: 72, zone_name: zoneName(72),
    stall_name: "ร้านตี๋ ปูทะเล", stall_number: "2/24", stall_type_name: "อาหารทะเล",
    date_start: "2024-06-30T17:00:00.000Z", date_end: "2027-07-28T17:00:00.000Z",
    lease_status: "ACTIVE", lease_status_name: "สัญญาเช่า",
    move_out_penalty_rate: 529.14, damage_insurance_limit: 4969,
  },
  {
    leaseAgreementId: 576, branchId: 1, userId: MOCK_CUSTOMER_USER_ID,
    lease_agreement_no: "ตลาดสด 90/2567", tenant_name: MOCK_CUSTOMER_NAME,
    stallId: 1602, zoneId: 65, zone_name: zoneName(65),
    stall_name: "ร้านตี๋ ของฝาก", stall_number: "3/07", stall_type_name: "ของฝาก",
    date_start: "2023-03-31T17:00:00.000Z", date_end: "2026-03-30T17:00:00.000Z",
    lease_status: "INACTIVE", lease_status_name: "ปิดใช้งาน",
    move_out_penalty_rate: 289, damage_insurance_limit: 2700,
  },
  {
    leaseAgreementId: 601, branchId: 1, userId: MOCK_CUSTOMER_USER_ID,
    lease_agreement_no: "ตลาดสด 12/2566", tenant_name: MOCK_CUSTOMER_NAME,
    stallId: 1700, zoneId: 80, zone_name: zoneName(80),
    stall_name: "ร้านตี๋ ทะเลสด", stall_number: "6/12", stall_type_name: "อาหารทะเล",
    date_start: "2023-01-31T17:00:00.000Z", date_end: "2026-01-30T17:00:00.000Z",
    lease_status: "TRANSFER", lease_status_name: "โอนสิทธิ์แล้ว",
    move_out_penalty_rate: 450, damage_insurance_limit: 3800,
  },
  {
    leaseAgreementId: 610, branchId: 1, userId: MOCK_CUSTOMER_USER_ID,
    lease_agreement_no: "ตลาดสด 45/2566", tenant_name: MOCK_CUSTOMER_NAME,
    // สัญญาที่โอนสิทธิ/ยกเลิกแล้ว backend อาจหาแผงไม่เจอ -> null ตาม spec ข้อ 3.5
    stallId: null, zoneId: null, zone_name: null,
    stall_name: null, stall_number: null, stall_type_name: null,
    date_start: "2022-09-30T17:00:00.000Z", date_end: "2025-09-29T17:00:00.000Z",
    lease_status: "CANCEL", lease_status_name: "ยกเลิกสัญญา",
    move_out_penalty_rate: 320, damage_insurance_limit: 2900,
  },
];

const OWNERS: DepositRefundOwner[] = [
  {
    userId: MOCK_CUSTOMER_USER_ID, customerId: MOCK_CUSTOMER_ID,
    owner_fullname: `นาย${MOCK_CUSTOMER_NAME}`,
    idCard: "577xxxxxxx720", mobilePhone: "081xxx3xxx", lease_count: "4",
  },
  {
    userId: 701, customerId: 505, owner_fullname: "นางวสีรจิตร จันทร์ขจร",
    idCard: "310xxxxxxx118", mobilePhone: "089xxx1xxx", lease_count: "1",
  },
  {
    userId: 702, customerId: 506, owner_fullname: "นายประยุร บัวสุ่ม",
    idCard: "345xxxxxxx757", mobilePhone: "082xxx7xxx", lease_count: "1",
  },
];

/* ------------------------------------------------------------------ *
 * helper
 * ------------------------------------------------------------------ */

/** ตัดเวลาออกให้เทียบเฉพาะวัน — date_from/date_to ที่หน้าจอส่งมาเป็น 'YYYY-MM-DD' */
const dateOnly = (value?: string | null) => String(value || "").slice(0, 10);

const matchFilter = (row: DepositRefundRow, params: DepositRefundParams) => {
  const keyword = String(params.keyword || "").trim().toLowerCase();
  if (keyword) {
    const haystack = [
      row.deposit_refund_no,
      row.lease_agreement_no,
      row.tenant_name,
      row.zone_name,
      row.stall_name,
      row.stall_number,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(keyword)) return false;
  }
  if (params.status && row.status !== params.status) return false;
  if (params.zoneId && row.zoneId !== params.zoneId) return false;
  if (params.stallId && row.stallId !== params.stallId) return false;
  if (params.date_from && dateOnly(row.request_date) < dateOnly(params.date_from)) return false;
  if (params.date_to && dateOnly(row.request_date) > dateOnly(params.date_to)) return false;
  return true;
};

/** ลูกค้าเห็นได้เฉพาะคำร้องของตัวเอง — API จริง scope จาก token */
const scopedRows = (side: DepositRefundSide): DepositRefundRow[] =>
  store
    .filter((row) => (side === "CUSTOMER" ? row.userId === MOCK_CUSTOMER_USER_ID : true))
    .map(toRow)
    .sort((a, b) => b.id_deposit_refund - a.id_deposit_refund);

const paginate = (
  rows: DepositRefundRow[],
  params: DepositRefundParams
): Paginated<DepositRefundRow> => {
  const limit = Number(params.limit) > 0 ? Number(params.limit) : 10;
  const page = Number(params.page) > 0 ? Number(params.page) : 1;
  const items = rows.slice((page - 1) * limit, page * limit);
  return {
    items: clone(items),
    meta: {
      totalItems: rows.length,
      itemCount: items.length,
      itemsPerPage: limit,
      totalPages: Math.max(1, Math.ceil(rows.length / limit)),
      currentPage: page,
    },
    links: { first: "", previous: "", next: "", last: "" },
  };
};

const findRow = (side: DepositRefundSide, id: number) =>
  store.find(
    (row) =>
      row.id_deposit_refund === id &&
      (side === "CUSTOMER" ? row.userId === MOCK_CUSTOMER_USER_ID : true)
  );

/** ปัด 2 ตำแหน่งแบบเดียวกับ backend (DECIMAL(12,2)) ไม่งั้นยอดที่ front รวมจะไม่ตรง */
const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * ข้อความบอกเหตุที่ยื่นไม่ได้ — helper ตัวเดียวใช้ทั้ง blocked_reason,
 * skipped[].reason และ errorDescription ของ 400 เหมือนที่ backend ทำ
 */
const blockedReason = (blocking: DepositRefundDetail) =>
  blocking.status === "APPROVE"
    ? `สัญญาเช่านี้ได้รับการคืนเงินมัดจำแล้ว (${blocking.deposit_refund_no})`
    : `สัญญาเช่านี้มีคำร้องที่รอพิจารณาอยู่แล้ว (${blocking.deposit_refund_no})`;

/** สัญญาที่ยังยื่นไม่ได้ = มีคำร้อง WAITING หรือ APPROVE ค้างอยู่ */
const blockingRequest = (leaseAgreementId: number) =>
  store.find(
    (row) =>
      row.leaseAgreementId === leaseAgreementId &&
      (row.status === "WAITING" || row.status === "APPROVE")
  );

const toLease = (lease: SeedLease): DepositRefundLease => {
  const blocking = blockingRequest(lease.leaseAgreementId);
  return {
    ...lease,
    deposit_amount: round2(lease.move_out_penalty_rate + lease.damage_insurance_limit),
    blocked_by_no: blocking?.deposit_refund_no || null,
    blocked_by_status: blocking?.status || null,
    // ใช้ helper ตัวเดียวกับ skipped[].reason และ 400 ของ POST ให้ถ้อยคำตรงกันทุกทาง
    blocked_reason: blocking ? blockedReason(blocking) : null,
    can_request: !blocking,
  };
};

/* ------------------------------------------------------------------ *
 * API surface — signature ต้องตรงกับ deposit-refund.api.ts ทุกเมธอด
 * ------------------------------------------------------------------ */

export default class DepositRefundMockApi {
  static async findAll(
    side: DepositRefundSide,
    params: DepositRefundParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<DepositRefundRow>>>> {
    await sleep();
    return ok(paginate(scopedRows(side).filter((row) => matchFilter(row, params)), params));
  }

  static async summary(
    side: DepositRefundSide,
    params: Omit<DepositRefundParams, "status" | "page" | "limit">
  ): Promise<AxiosResponse<BaseResponse<DepositRefundSummary>>> {
    await sleep();
    const rows = scopedRows(side).filter((row) => matchFilter(row, params));
    const countBy = (status: DepositRefundStatus) =>
      rows.filter((row) => row.status === status).length;
    return ok({
      all: rows.length,
      WAITING: countBy("WAITING"),
      APPROVE: countBy("APPROVE"),
      NOT_APPROVE: countBy("NOT_APPROVE"),
      CANCELED: countBy("CANCELED"),
    });
  }

  static async filterZone(
    side: DepositRefundSide,
    _branchId?: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundZoneOption[]>>> {
    await sleep();
    // option มาจากคำร้องที่มีอยู่จริง ไม่ใช่ master ทั้งระบบ (ตาม spec ข้อ 3.3)
    const zoneIds = new Set(scopedRows(side).map((row) => row.zoneId));
    return ok(ZONES.filter((zone) => zoneIds.has(zone.zoneId)));
  }

  static async filterStall(
    side: DepositRefundSide,
    _branchId?: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundStallOption[]>>> {
    await sleep();
    const stallMap = new Map<number, DepositRefundStallOption>();
    scopedRows(side).forEach((row) => {
      if (row.stallId !== null && !stallMap.has(row.stallId)) {
        stallMap.set(row.stallId, {
          stallId: row.stallId,
          stall_name: row.stall_name,
          stall_number: row.stall_number,
          // zoneId ตัวเดียวกับที่ใช้กรอง ?zoneId= เพื่อให้ cascading ตรงกับตาราง
          zoneId: row.zoneId,
        });
      }
    });
    return ok(Array.from(stallMap.values()));
  }

  static async findById(
    side: DepositRefundSide,
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundDetail>>> {
    await sleep();
    const row = findRow(side, id);
    if (!row) throw fail(404, "ไม่พบคำร้องขอคืนค่ามัดจำที่ต้องการ");
    return ok(clone(row));
  }

  static async log(
    side: DepositRefundSide,
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundLog[]>>> {
    await sleep();
    if (!findRow(side, id)) throw fail(404, "ไม่พบคำร้องขอคืนค่ามัดจำที่ต้องการ");
    return ok(
      clone(
        logStore
          .filter((log) => log.id_deposit_refund === id)
          .sort((a, b) => b.id_deposit_refund_log - a.id_deposit_refund_log)
      )
    );
  }

  static async fileAll(
    side: DepositRefundSide,
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundFile[]>>> {
    await sleep();
    if (!findRow(side, id)) throw fail(404, "ไม่พบคำร้องขอคืนค่ามัดจำที่ต้องการ");
    return ok(
      clone(
        logStore
          .filter((log) => log.id_deposit_refund === id)
          .flatMap((log) => log.files)
      )
    );
  }

  static async ownerSearch(params: {
    keyword?: string;
    branchId?: number;
    only_has_deposit_lease?: 0 | 1;
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<BaseResponse<Paginated<DepositRefundOwner>>>> {
    await sleep();
    const keyword = String(params.keyword || "").replace(/-/g, "").trim().toLowerCase();
    const items = OWNERS.filter((owner) => {
      if (!keyword) return true;
      return `${owner.owner_fullname} ${owner.idCard || ""}`.toLowerCase().includes(keyword);
    });
    const limit = Number(params.limit) > 0 ? Number(params.limit) : 10;
    const page = Number(params.page) > 0 ? Number(params.page) : 1;
    const paged = items.slice((page - 1) * limit, page * limit);
    return ok({
      items: clone(paged),
      meta: {
        totalItems: items.length,
        itemCount: paged.length,
        itemsPerPage: limit,
        totalPages: Math.max(1, Math.ceil(items.length / limit)),
        currentPage: page,
      },
      links: { first: "", previous: "", next: "", last: "" },
    });
  }

  static async ownerLease(
    userId: number,
    _branchId?: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundLease[]>>> {
    await sleep();
    // mock มีสัญญาละเอียดของผู้เช่า 548 คนเดียว คนอื่นคืนลิสต์ว่าง
    if (userId !== MOCK_CUSTOMER_USER_ID) return ok([]);
    return ok(clone(LEASES.map(toLease)));
  }

  static async myLease(): Promise<AxiosResponse<BaseResponse<DepositRefundLease[]>>> {
    await sleep();
    return ok(clone(LEASES.map(toLease)));
  }

  static async create(
    body: CreateDepositRefundBody
  ): Promise<AxiosResponse<BaseResponse<CreateDepositRefundResult>>> {
    await sleep();

    const ids = body?.leaseAgreementIds || [];
    if (!ids.length) throw fail(400, "กรุณาเลือกสัญญาเช่าอย่างน้อย 1 สัญญา");

    const created: CreateDepositRefundItem[] = [];
    const skipped: { leaseAgreementId: number; reason: string }[] = [];

    ids.forEach((leaseAgreementId) => {
      const lease = LEASES.find((item) => item.leaseAgreementId === leaseAgreementId);
      if (!lease) {
        skipped.push({
          leaseAgreementId,
          reason: "ไม่พบสัญญาเช่าที่มีเงินมัดจำของผู้เช่ารายนี้ หรือสถานะสัญญาขอคืนเงินมัดจำไม่ได้",
        });
        return;
      }
      const blocking = blockingRequest(leaseAgreementId);
      if (blocking) {
        skipped.push({ leaseAgreementId, reason: blockedReason(blocking) });
        return;
      }
      created.push(pushRequest(lease, body.refund_method || null, body.note || null, true));
    });

    // ไม่ได้สร้างเลยแม้แต่ใบเดียว -> 400 พร้อมเหตุผลทุกใบต่อด้วย ' · ' ตาม spec
    if (!created.length) {
      throw fail(400, skipped.map((item) => item.reason).join(" · "));
    }

    return ok({ created, skipped });
  }

  static async createRequest(
    body: CreateDepositRefundRequestBody
  ): Promise<AxiosResponse<BaseResponse<CreateDepositRefundItem>>> {
    await sleep();

    const lease = LEASES.find((item) => item.leaseAgreementId === body?.leaseAgreementId);
    if (!lease) {
      throw fail(
        400,
        "ไม่พบสัญญาเช่าที่มีเงินมัดจำของผู้เช่ารายนี้ หรือสถานะสัญญาขอคืนเงินมัดจำไม่ได้"
      );
    }

    const blocking = blockingRequest(lease.leaseAgreementId);
    if (blocking) throw fail(400, blockedReason(blocking));

    return ok(pushRequest(lease, body.refund_method || null, body.note || null, false));
  }

  static async review(
    id: number,
    body: FormData
  ): Promise<AxiosResponse<BaseResponse<any>>> {
    await sleep();

    const row = store.find((item) => item.id_deposit_refund === id);
    if (!row) throw fail(404, "ไม่พบคำร้องขอคืนค่ามัดจำที่ต้องการ");
    if (row.status === "CANCELED") throw fail(400, "คำร้องนี้ถูกยกเลิกไปแล้ว");
    if (row.status !== "WAITING") throw fail(400, "คำร้องนี้ถูกพิจารณาไปแล้ว");

    const result = String(body.get("result") || "");
    if (result !== "APPROVE" && result !== "NOT_APPROVE") {
      throw fail(400, "กรุณาเลือกผลการพิจารณา");
    }

    const toNumber = (value: FormDataEntryValue | null) => {
      const amount = Number(String(value ?? "").replace(/,/g, "").trim());
      return Number.isFinite(amount) && amount > 0 ? amount : 0;
    };

    const note = String(body.get("note") || "") || null;
    const reviewedDate = new Date().toISOString();
    const isApprove = result === "APPROVE";

    // ส่งมา = เขียนทับ, ไม่ส่ง = คงค่าเดิมที่รับไว้ตอนยื่น (ไม่ล้างเป็น null) — spec v2 ข้อ B4
    const methodInput = String(body.get("refund_method") || "");
    if (methodInput === "BANK_TRANSFER" || methodInput === "CASH" || methodInput === "CHEQUE") {
      row.refund_method = methodInput;
      row.refund_method_name = METHOD_NAME[methodInput];
    }

    let deducts: DepositRefundDeduct[] = [];
    if (isApprove) {
      const lines: [DepositRefundDeduct["deduct_type"], number, number][] = [
        [
          "MOVE_OUT_PENALTY",
          row.move_out_penalty_rate,
          toNumber(body.get("deduct_move_out")),
        ],
        [
          "DAMAGE_INSURANCE",
          row.damage_insurance_limit,
          toNumber(body.get("deduct_damage")),
        ],
      ];
      deducts = lines.map(([type, base, deduct], index) => {
        // server clamp ยอดหักไม่ให้เกินยอดตั้งต้น
        const applied = Math.min(deduct, base);
        return {
          id_deposit_refund_deduct: id * 10 + index,
          seq: index + 1,
          deduct_type: type,
          label: DEDUCT_LABEL[type],
          base_amount: dec(base),
          deduct_amount: dec(applied),
          remain_amount: dec(base - applied),
        };
      });
    }

    const approved = isApprove
      ? dec(deducts.reduce((sum, line) => sum + Number(line.remain_amount), 0))
      : null;
    const refundDate = isApprove ? String(body.get("refund_date") || "") || null : null;
    const docRef = isApprove ? String(body.get("doc_ref") || "") || null : null;

    row.status = result;
    row.reviewed_date = reviewedDate;
    row.reviewed_id = MOCK_STAFF_ID;
    row.reviewed_name = MOCK_STAFF_NAME;
    row.review_note = note;
    row.approved_amount = approved;
    // detail คืน refund_date เป็น ISO (ต่างจาก log ที่คืน 'YYYY-MM-DD')
    row.refund_date = refundDate ? `${refundDate}T17:00:00.000Z` : null;
    row.doc_ref = docRef;
    row.deducts = deducts;

    const files: DepositRefundFile[] = body.getAll("files").map((entry) => {
      nextFileId += 1;
      const upload = entry as File;
      uploadedFiles.set(nextFileId, upload);
      return {
        id_deposit_refund_file: nextFileId,
        file_name: `${Date.now()}-${upload?.name || "file"}`,
        mimetype: upload?.type || null,
        file_size: String(upload?.size ?? 0),
        download_url: `/deposit-refund/${id}/file/${nextFileId}/download`,
      };
    });

    nextLogId += 1;
    logStore.push({
      id_deposit_refund_log: nextLogId,
      id_deposit_refund: id,
      action: "REVIEW",
      result,
      note,
      approved_amount: approved,
      refund_date: refundDate,
      doc_ref: docRef,
      action_id: MOCK_STAFF_ID,
      action_name: MOCK_STAFF_NAME,
      action_date: reviewedDate,
      deducts,
      files,
    });

    // response ของเส้นนี้คืน number (ไม่ใช่ string เหมือน GET) ตาม spec ข้อ 3.8
    return ok({
      id_deposit_refund: id,
      status: result,
      refund_method: row.refund_method,
      approved_amount: approved === null ? null : Number(approved),
      deducts: deducts.map((line) => ({
        ...line,
        base_amount: Number(line.base_amount),
        deduct_amount: Number(line.deduct_amount),
        remain_amount: Number(line.remain_amount),
      })),
    });
  }

  static async remove(id: number): Promise<AxiosResponse<BaseResponse<boolean>>> {
    await sleep();

    const index = store.findIndex((row) => row.id_deposit_refund === id);
    if (index < 0) throw fail(404, "ไม่พบคำร้องขอคืนค่ามัดจำที่ต้องการ");
    if (store[index].status !== "WAITING") {
      throw fail(400, "ลบได้เฉพาะคำร้องที่ยังรอพิจารณาเท่านั้น");
    }
    store.splice(index, 1);

    return ok(true);
  }

  static async cancel(id: number): Promise<AxiosResponse<BaseResponse<boolean>>> {
    await sleep();

    const row = findRow("CUSTOMER", id);
    if (!row) throw fail(404, "ไม่พบคำร้องขอคืนค่ามัดจำที่ต้องการ");
    if (row.status !== "WAITING") {
      throw fail(400, "ยกเลิกได้เฉพาะคำร้องที่ยังรอเจ้าหน้าที่พิจารณาเท่านั้น");
    }

    row.status = "CANCELED";
    nextLogId += 1;
    logStore.push({
      id_deposit_refund_log: nextLogId,
      id_deposit_refund: id,
      action: "CANCEL",
      result: null,
      note: null,
      action_id: row.userId,
      action_name: row.tenant_name,
      action_date: new Date().toISOString(),
      deducts: [],
      files: [],
    });

    return ok(true);
  }

  /**
   * ตารางใบเสร็จ — mock สร้างจากคำร้องใน store ให้ได้ทั้ง 2 ที่มา (INVOICE/DEPOSIT)
   * ไม่ได้เลียนแบบข้อมูลใบแจ้งหนี้จริง แค่พอให้ตารางกับปุ่มดาวน์โหลดมีของให้กด
   * และให้เจอสภาพจริงข้อสำคัญ: refId ซ้ำกันข้าม source, receipt_no ของ DEPOSIT เป็น null
   */
  static async receipt(
    params: DepositRefundReceiptParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<DepositRefundReceiptRow>>>> {
    await sleep();

    // กรองที่ระดับคำร้องก่อน เพราะ userId ไม่ได้อยู่ในแถวใบเสร็จที่ API คืน
    const requests = scopedRows("ADMIN").filter((row) => {
      if (params.userId && row.userId !== Number(params.userId)) return false;
      if (params.stallId && row.stallId !== Number(params.stallId)) return false;
      if (params.zoneId && row.zoneId !== Number(params.zoneId)) return false;
      return true;
    });

    const rows: DepositRefundReceiptRow[] = [];
    requests.forEach((row) => {
      const shared = {
        tenant_name: row.tenant_name,
        zoneId: row.zoneId,
        zone_name: row.zone_name,
        stallId: row.stallId,
        stall_name: row.stall_name,
        stall_number: row.stall_number,
        document_date: row.request_date,
        branchId: Number(params.branchId) || 1,
      };
      rows.push({
        ...shared,
        source: "INVOICE",
        refId: row.id_deposit_refund,
        document_no: `111690${1000 + row.id_deposit_refund}`,
        receipt_no: `690700${row.id_deposit_refund}`,
        status: "PAY_APPROVE",
        source_name: "ใบแจ้งหนี้",
        download_url: `invoice/${row.id_deposit_refund}/receipt`,
      });
      rows.push({
        ...shared,
        source: "DEPOSIT",
        refId: row.id_deposit_refund,
        document_no: row.deposit_refund_no,
        receipt_no: null,
        status: "SUCCESS",
        source_name: "ใบคำร้องขอชำระเงินมัดจำ",
        download_url: `deposit/${row.id_deposit_refund}/receipt`,
      });
    });

    const keyword = String(params.keyword || "").trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (params.source && row.source !== params.source) return false;
      if (!keyword) return true;
      return [row.document_no, row.receipt_no, row.tenant_name, row.stall_name, row.stall_number]
        .some((value) => String(value || "").toLowerCase().includes(keyword));
    });

    // เพดาน 100 เหมือนของจริง (backend clamp ให้ ส่งเกินก็ได้แค่ 100)
    const limit = Math.min(Number(params.limit) > 0 ? Number(params.limit) : 10, 100);
    const page = Number(params.page) > 0 ? Number(params.page) : 1;
    const items = filtered.slice((page - 1) * limit, page * limit);

    return ok({
      items: clone(items),
      meta: {
        totalItems: filtered.length,
        itemCount: items.length,
        itemsPerPage: limit,
        totalPages: Math.max(1, Math.ceil(filtered.length / limit)),
        currentPage: page,
      },
      links: { first: "", previous: "", next: "", last: "" },
    });
  }

  /**
   * ยอดหักตั้งต้นจากคำสั่งเรียกคืนแผงค้า
   * mock ไม่มีตารางคำสั่งเรียกคืน จึงสมมติให้คำร้องที่ MOCK_DEDUCT_DEFAULT_ID เท่านั้น
   * ที่มีค่าตั้งต้น ใบอื่นคืน has_default = false เพื่อให้เทสต์ได้ทั้ง 2 ทาง
   */
  static async deductDefault(
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundDeductDefault>>> {
    await sleep();

    const row = store.find((item) => item.id_deposit_refund === id);
    if (!row) throw fail(404, "ไม่พบคำร้องขอคืนค่ามัดจำที่ต้องการ");

    const moveOutBase = round2(row.move_out_penalty_rate);
    const damageBase = round2(row.damage_insurance_limit);

    // ใบตัวอย่างที่สมมติว่าเคยถูกเรียกคืนแผงค้าและหักไว้จริง
    const hasSource = id === MOCK_DEDUCT_DEFAULT_ID;
    const moveOutDeduct = hasSource ? Math.min(100, moveOutBase) : 0;
    const damageDeduct = hasSource ? Math.min(200, damageBase) : 0;
    const deductTotal = round2(moveOutDeduct + damageDeduct);

    const line = (
      seq: number,
      type: DepositRefundDeduct["deduct_type"],
      base: number,
      deduct: number
    ) => ({
      seq,
      deduct_type: type,
      label: DEDUCT_LABEL[type],
      base_amount: base,
      deduct_amount: deduct,
      remain_amount: round2(base - deduct),
      // mock ไม่มีเคสที่ยอดดิบเกินเพดาน จึงเท่ากับ deduct_amount เสมอ
      source_deduct_amount: deduct,
    });

    return ok({
      id_deposit_refund: row.id_deposit_refund,
      deposit_refund_no: row.deposit_refund_no,
      leaseAgreementId: row.leaseAgreementId,
      lease_agreement_no: row.lease_agreement_no,
      status: row.status,
      // has_default = true ก็ต่อเมื่อมีคำสั่งอนุมัติแล้ว "และ" ยอดหักรวม > 0
      has_default: hasSource && deductTotal > 0,
      deduct_move_out: moveOutDeduct,
      deduct_damage: damageDeduct,
      deduct_total: deductTotal,
      approved_amount: round2(moveOutBase - moveOutDeduct + (damageBase - damageDeduct)),
      deducts: [
        line(1, "MOVE_OUT_PENALTY", moveOutBase, moveOutDeduct),
        line(2, "DAMAGE_INSURANCE", damageBase, damageDeduct),
      ],
      source: hasSource
        ? {
            id_deposit_refund: 9001,
            deposit_refund_no: "54324534",
            request_type: "STALL_RECLAIM",
            status: "APPROVE",
            due_date: "2026-09-11",
            reviewed_date: "2026-09-11T06:56:06.000Z",
            reviewed_name: MOCK_STAFF_NAME,
            review_note: "ตรวจรับคืนพื้นที่แล้ว",
            approved_amount: round2(moveOutBase - moveOutDeduct + (damageBase - damageDeduct)),
          }
        : null,
    });
  }

  static async downloadFile(downloadUrl: string): Promise<AxiosResponse<Blob>> {
    await sleep();

    const fileId = Number(downloadUrl.match(/\/file\/(\d+)\/download/)?.[1]);
    const uploaded = uploadedFiles.get(fileId);
    const blob =
      uploaded ||
      new Blob([`mock file for ${downloadUrl}`], { type: "text/plain;charset=utf-8" });

    return {
      data: blob as Blob,
      status: 200,
      statusText: "OK",
      headers: {},
      config: {},
    };
  }
}

/** สร้างใบคำร้องใหม่ลง store + log CREATE แล้วคืน item ตามรูป response ของ API */
function pushRequest(
  lease: SeedLease,
  refundMethod: DepositRefundMethod | null,
  note: string | null,
  byStaff: boolean
): CreateDepositRefundItem {
  nextRefundId += 1;
  nextRefundNo += 1;

  const id = nextRefundId;
  const no = `DEP-2569-${String(nextRefundNo).padStart(4, "0")}`;
  const requestDate = new Date().toISOString();
  const amount = lease.move_out_penalty_rate + lease.damage_insurance_limit;

  const detail: DepositRefundDetail = {
    id_deposit_refund: id,
    deposit_refund_no: no,
    branchId: lease.branchId,
    leaseAgreementId: lease.leaseAgreementId,
    lease_agreement_no: lease.lease_agreement_no,
    userId: lease.userId,
    customerId: MOCK_CUSTOMER_ID,
    tenant_name: lease.tenant_name,
    tenant_idCard: "577xxxxxxx720",
    tenant_mobilePhone: "081xxx3xxx",
    zoneId: lease.zoneId,
    zone_name: lease.zone_name,
    stallId: lease.stallId,
    stall_name: lease.stall_name,
    stall_number: lease.stall_number,
    stall_type_name: lease.stall_type_name,
    lease_date_end: lease.date_end,
    lease_status: lease.lease_status,
    lease_status_name: lease.lease_status_name,
    move_out_penalty_rate: lease.move_out_penalty_rate,
    damage_insurance_limit: lease.damage_insurance_limit,
    request_amount: dec(amount),
    refund_method: refundMethod,
    refund_method_name: refundMethod ? METHOD_NAME[refundMethod] : null,
    request_note: note,
    request_date: requestDate,
    request_by_id: byStaff ? MOCK_STAFF_ID : lease.userId,
    request_by_name: byStaff ? MOCK_STAFF_NAME : lease.tenant_name,
    reviewed_date: null,
    reviewed_id: null,
    reviewed_name: null,
    review_note: null,
    approved_amount: null,
    refund_date: null,
    doc_ref: null,
    status: "WAITING",
    deducts: [],
  };

  store.push(detail);

  nextLogId += 1;
  logStore.push({
    id_deposit_refund_log: nextLogId,
    id_deposit_refund: id,
    action: "CREATE",
    result: null,
    note,
    action_id: detail.request_by_id,
    action_name: detail.request_by_name,
    action_date: requestDate,
    deducts: [],
    files: [],
  });

  return {
    id_deposit_refund: id,
    deposit_refund_no: no,
    leaseAgreementId: lease.leaseAgreementId,
    lease_agreement_no: lease.lease_agreement_no,
    request_amount: dec(amount),
  };
}
